import './style.css'
import React, { useEffect, useRef, useState } from 'react'
import { ProjectProps } from '../Project'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom';
import { FunctionModel } from '../../../utils/interfaces';
import MainBlock from '../../../components/MainBlock';
import Loading from '../../../components/Loading';
import { TbCopy, TbExternalLink } from 'react-icons/tb';
import { useAsyncEffect } from '../../../utils/functions';
import { Admin, getItem, updateItem } from '../../../api/API';
import { freeLimits, proLimits, tables } from '../../../utils/constants';
import Editor from '@monaco-editor/react';
import FunctionTest from './FunctionTest';
import FunctionConfig from './FunctionConfig';
import FunctionEnv from './FunctionEnv';

const FunctionManage = (props: ProjectProps) => {

  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [func, setFunc] = useState<FunctionModel | null>(null);
  const [originCode, setOriginCode] = useState('');
  const [code, setCode] = useState('');
  const [option, setOption] = useState<'edit' | 'test' | 'config' | 'env'>('edit');
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [warningTime, setWarningTime] = useState(0);
  // test
  const testRef = useRef<HTMLDivElement>(null);
  const httpMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'];
  const [httpMethod, setHttpMethod] = useState('GET');
  const [queryParams, setQueryParams] = useState('');
  const [headers, setHeaders] = useState<{ name: string, value: string }[]>([]);
  const [body, setBody] = useState('');
  const [response, setResponse] = useState<{ statusCode: number, body: string } | null>(null);
  const [isResponding, setIsResponding] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [showHttpMethodDropdown, setShowHttpMethodDropdown] = useState(false);
  const testProps = { httpMethods, httpMethod, setHttpMethod, queryParams, setQueryParams, headers, setHeaders, body, setBody, response, setResponse, isResponding, setIsResponding, showHttpMethodDropdown, setShowHttpMethodDropdown };
  // env
  const [envs, setEnvs] = useState<{ [key: string]: string }>({});

  // auto save code
  useEffect(() => {
    if (!code || isUploading || code === originCode || !func?.autoSave) return;
    const timeout = setTimeout(async () => await autoUploadCode(), 3000);
    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useAsyncEffect(async () => {
    const func = (await getItem(tables.functions, params.serviceName || '')).item;
    const env = (await Admin.getFunctionEnvs(props.project.id, params.serviceName || ''))?.env || {};
    if (!func) {
      navigate(`/pagenotfound`);
      return;
    }
    setFunc(func);
    setEnvs(env);
    setOriginCode(func.code);
    setCode(func.code);
    setIsLoading(false);
  }, [params.serviceName]);

  useEffect(() => {
    if (warningTime > 0) {
      const interval = setInterval(() => {
        setWarningTime(warningTime - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [warningTime]);

  // auto scroll to bottom
  useEffect(() => {
    if (response?.statusCode) {
      testRef.current?.scrollTo({ top: testRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [response]);

  // editor on mount
  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  }

  // on editor text change
  const handleEditorTextChange = (value: string | undefined) => {
    if (!value) return;
    // check if reach limit
    if ((props.user?.plan.name === 'pro' && value?.length > proLimits.functionSize) || (props.user?.plan.name === 'free' && value.length > freeLimits.functionSize)) {
      setWarningTime(5);
      setTimeout(() => {
        if ((editorRef.current as any).getValue() !== code) {
          (editorRef.current as any).setValue(code);
        }
      }, 0);
      return;
    }
    setCode(value);
  }

  // upload code
  const uploadCode = async () => {
    setIsUploading(true);
    const newFunction: FunctionModel = { ...func!, code, lastUpdate: new Date().toISOString() };
    await updateItem(tables.functions, func!.id, { code, lastUpdate: new Date().toISOString() });
    setFunc(newFunction);
    setOriginCode(code);
    setIsUploading(false);
  }

  const autoUploadCode = async () => {
    setIsAutoSaving(true);
    await uploadCode();
    setIsAutoSaving(false);
  }

  return (
    (!func || isLoading) ? <main className='flex items-center justify-center flex-1'><Loading solid size={8} /></main>
      : <>
        <div className='flex-1 flex flex-col p-4 md:px-8 overflow-auto'>
          <div className='flex items-center justify-between overflow-hidden gap-4'>
            <div className='flex items-center flex-1 overflow-hidden'>
              <button onClick={() => navigate(`/project/${props.project.id}/function`)} className='h-8 w-8 flex items-center justify-center rounded-full mr-2'><i className='fa-solid fa-arrow-left' /></button>
              <h1 className='flex-1 md:text-2xl whitespace-nowrap overflow-hidden text-ellipsis'>{func?.name}</h1>
            </div>
          </div>
          <div className='flex-1 overflow-auto mt-4 flex flex-col'>
            <MainBlock extraclass='flex-1 flex flex-col overflow-auto p-4'>
              <div className='pt-2 flex justify-between items-start flex-wrap gap-4'>
                <div>
                  <div className='flex items-center'><div>ID: <span>{func?.id}</span></div><button onClick={() => navigator.clipboard.writeText(func?.id || '')} className='ml-2 w-6 h-6 flex items-center justify-center cursor-pointer text-gray-400 hover:text-primary rounded-md'><TbCopy /></button></div>
                </div>
              </div>
              <div className='border-t my-2' />
              <div className='flex-1 flex flex-col md:flex-row py-4 gap-4 overflow-auto'>
                <div className='flex flex-row md:flex-col md:min-w-48 overflow-auto gap-1 *:whitespace-nowrap'>
                  <button onClick={() => setOption('edit')} className={`${option === 'edit' ? 'bg-primary/10 text-primary' : ''} py-2 px-8 md:px-4 text-start hover:bg-primary/10 rounded-lg text-xs`}>{t('general.edit')}</button>
                  <button onClick={() => setOption('test')} className={`${option === 'test' ? 'bg-primary/10 text-primary' : ''} py-2 px-8 md:px-4 text-start hover:bg-primary/10 rounded-lg text-xs`}>{t('general.test')}</button>
                  <button onClick={() => setOption('env')} className={`${option === 'env' ? 'bg-primary/10 text-primary' : ''} py-2 px-8 md:px-4 text-start hover:bg-primary/10 rounded-lg text-xs`}>{t('project.function.env')}</button>
                  <button onClick={() => setOption('config')} className={`${option === 'config' ? 'bg-primary/10 text-primary' : ''} py-2 px-8 md:px-4 text-start hover:bg-primary/10 rounded-lg text-xs`}>{t('general.config')}</button>
                  <div className='border-t-0 md:border-t border-l md:border-l-0 m-1' />
                  {isAutoSaving && <div className='text-gray-400 text-xs font-light'>Auto saving...</div>}
                  <button onClick={uploadCode} disabled={code === originCode || isUploading} className={`bg-primary disabled:bg-gray-300 flex items-center justify-center text-white h-8 px-8 md:px-4 rounded-lg text-xs`}>{isUploading ? <Loading /> : t('general.upload')}</button>
                </div>
                {/* editor */}
                {option === 'edit' ? <div className='editor flex-1 border rounded-lg overflow-hidden'>
                  <Editor
                    height={"100%"}
                    width={"100%"}
                    defaultLanguage={func?.language || 'javascript'}
                    value={code}
                    onMount={handleEditorDidMount}
                    onChange={handleEditorTextChange}
                  />
                {/* test */}
                </div> : option === 'test' ? <div ref={testRef} className='flex-1 overflow-auto'>
                  <FunctionTest {...props} {...testProps} func={func} />
                {/* config */}
                </div> : option === 'config' ? <div className='flex-1 overflow-auto'>
                  <FunctionConfig {...props} func={func} setFunc={setFunc} />
                {/* env */}
                </div> : option === 'env' ? <div className='flex-1 overflow-auto'>
                  <FunctionEnv {...props} func={func} envs={envs} setEnvs={setEnvs} />
                </div> : <></>}
              </div>
            </MainBlock>
          </div>
        </div>

        {warningTime > 0 && <div className='z-40 text-sm fixed top-20 left-1/2 -translate-x-1/2 flex items-center justify-center text-white rounded-full px-8 py-2 bg-amber-500'>{t('project.function.size-warning')}</div>}
      </>
  )
}

export default FunctionManage